/**
 * <s-product-gallery> — Sliding product gallery with an optional Model / Product view switch.
 *
 * Markup contract:
 *   [data-gallery-track]   — horizontal scroll-snap track; each slide is a direct child with data-view
 *   [data-view]            — 'model' or 'product' on each slide
 *   [data-gallery-switch]  — optional role="switch" button; aria-checked="true" shows model slides
 *   [data-gallery-prev] / [data-gallery-next] — step buttons
 *   [data-gallery-current] / [data-gallery-total] — counter outputs
 *   [data-gallery-empty]   — optional message shown when the active view has no slides (editor only)
 *
 * Without a switch every slide is shown. Motion respects prefers-reduced-motion.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #switch;
  #prev;
  #next;
  #current;
  #total;
  #empty;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.querySelectorAll(':scope > [data-view]'));
    this.#switch = this.querySelector('[data-gallery-switch]');
    this.#prev = this.querySelector('[data-gallery-prev]');
    this.#next = this.querySelector('[data-gallery-next]');
    this.#current = this.querySelector('[data-gallery-current]');
    this.#total = this.querySelector('[data-gallery-total]');
    this.#empty = this.querySelector('[data-gallery-empty]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#switch?.addEventListener('click', () => this.#toggle(), opts);
    this.#prev?.addEventListener('click', () => this.#step(-1), opts);
    this.#next?.addEventListener('click', () => this.#step(1), opts);
    this.#track.addEventListener('scroll', () => this.#queueUpdate(), { ...opts, passive: true });
    this.#track.addEventListener('keydown', (event) => this.#onKey(event), opts);
    window.addEventListener('resize', () => this.#queueUpdate(), { ...opts, passive: true });

    if (this.#switch) {
      this.#applyView(this.#switch.getAttribute('aria-checked') === 'true' ? 'model' : 'product');
    } else {
      this.#applyView('all');
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #visible() {
    return this.#slides.filter((slide) => !slide.hidden);
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #toggle() {
    const nextChecked = this.#switch.getAttribute('aria-checked') !== 'true';
    this.#switch.setAttribute('aria-checked', String(nextChecked));
    this.#applyView(nextChecked ? 'model' : 'product');
  }

  #applyView(view) {
    for (const slide of this.#slides) {
      slide.hidden = view !== 'all' && slide.dataset.view !== view;
    }
    if (this.#empty) this.#empty.hidden = this.#visible.length > 0;
    this.#track.scrollTo({ left: 0, behavior: 'auto' });
    this.#update();
  }

  #index() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #step(direction) {
    const isRtl = getComputedStyle(this.#track).direction === 'rtl';
    const delta = this.#track.clientWidth * direction * (isRtl ? -1 : 1);
    this.#track.scrollBy({ left: delta, behavior: this.#behavior });
  }

  #onKey(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    }
  }

  #queueUpdate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const total = this.#visible.length;
    const index = Math.min(this.#index(), Math.max(total - 1, 0));
    const pad = (value) => String(value).padStart(2, '0');
    if (this.#current) this.#current.textContent = pad(total ? index + 1 : 0);
    if (this.#total) this.#total.textContent = pad(total);
    if (this.#prev) this.#prev.disabled = index <= 0;
    if (this.#next) this.#next.disabled = index >= total - 1;
    this.toggleAttribute('data-single', total <= 1);
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
